import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Trash2 } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import ExportButtons from './ExportButtons';
import DateFilter, { DateInput, formatDisplayDate } from './DateFilter';
import InterviewLinkCell from './InterviewLinkCell';
import GlobalFilters from './GlobalFilters';
import { defaultGlobalFilters } from '../utils/filterUtils';
import CandidateKPIDetailsGrid from './CandidateKPIDetailsGrid';
import { usePagedQuery, dateRangeParams } from './usePagedQuery';
import { Pagination, SearchBox } from './TableControls';

const API = process.env.REACT_APP_BACKEND_API_URL;

// The exact 17-value Interview Stage list. Some entries share an "- Interview"
// suffix even though they mean opposite things (e.g. Prescreen Select vs
// Prescreen Reject both say "- Interview"), so bucket assignment below is
// semantic (by keyword), not a literal suffix match.
const STAGE_REMARK_OPTIONS = [
    'Awaiting Prescreen Slot - Interview Awaiting',
    'Prescreen Scheduled - Interview',
    'Prescreen Reject - Interview',
    'Glider Shared - Interview',
    'Glider Reject - Interview',
    'Awaiting Tech Round Slot - Interview Awaiting',
    'Tech Round Scheduled - Interview',
    'Tech Interview Reject - Interview',
    'Tech Interview Feedback - Pending',
    'Awaiting Client Round Slot - Interview Awaiting',
    'Client Round Select - Interview',
    'Client Round scheduled - Interview',
    'Client Round Reject - Interview',
    'Client Screen Reject - Interview',
    'Client Round Feedback - Pending',
    'Selected for Role - Client Onboarded Pending',
    'Onboarded Joined Company - Onboarded',
    'Evanti Screen Reject - Interview',
    'Candidate Back out',
    'Role Closed - Hold',
    'Role Hold - Hold',
    'Duplicate Profile',
];

// Item #4: the Call Remark dropdown. Item #1: these three also auto-reassign
// the task on the backend the moment they're set (kept in sync here by a refetch).
const CALL_REMARK_OPTIONS = ['Interested', 'Not Interested', 'Not Eligible', 'Not Picked Up', 'Not Reachable', 'Call Later', '60 Days Notice', '90 Days Notice', "Number Doesn't Exist",];
// const AUTO_REASSIGN_REMARKS = ['Not Picked Up', 'Not Reachable', 'Call Later'];
const selectStyle = { padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', minWidth: '150px' };
const smallSelectStyle = { ...selectStyle, minWidth: '140px', padding: '5px 7px', fontSize: '11px' };

const AdminInterviews = ({ user, onLogout }) => {
    const navigate = useNavigate();

    const [savingStageId, setSavingStageId] = useState(null);
    const [deleteConfirmCandidate, setDeleteConfirmCandidate] = useState(null);
    const [deletingCandidate, setDeletingCandidate] = useState(false);

    const [candidateSubTab, setCandidateSubTab] = useState('interviews');
    const [candFilterRemark, setCandFilterRemark] = useState('');
    const [candFilterJobRole, setCandFilterJobRole] = useState('');
    const [candFilterVendorName, setCandFilterVendorName] = useState('');
    const [candFilterEvantiSpoc, setCandFilterEvantiSpoc] = useState('');
    const [candFilterTeamLead, setCandFilterTeamLead] = useState('');
    const [candFilterRecruiter, setCandFilterRecruiter] = useState('');

    // Global filters — Company Name, Company SPOC, Received Date, Action Date. They scope the
    // data BEFORE it is split into the four tabs, so they apply to all of them (incl. KPI Details).
    const [globalFilters, setGlobalFilters] = useState(() => defaultGlobalFilters('all'));
    // Interview Date (Interviews tab) — the same DateFilter used everywhere else.
    const [interviewDateFilter, setInterviewDateFilter] = useState({ mode: 'All' });
    const [resetSignal, setResetSignal] = useState(0);

    // ---- Server-side paging / filtering (GET /api/candidates) -----------------------------------------
    // The API applies ownership scope, the global filters, the tab (bucket), every filter below and the sort
    // (newest first), and returns one page plus the tab counts. Any filter change goes back to page 1.
    const [candSearch, setCandSearch] = useState('');
    const [entryDateFilter, setEntryDateFilter] = useState({ mode: 'All' });

    const candParams = useMemo(() => ({
        bucket: candidateSubTab === 'kpi' ? 'all' : candidateSubTab,
        search: candSearch.trim(),
        company: globalFilters.company, client_spoc: globalFilters.spoc,
        ...dateRangeParams(globalFilters.receivedDate, 'received'),
        ...dateRangeParams(globalFilters.actionDate, 'action'),
        stage_remark: candFilterRemark, job_role: candFilterJobRole, vendor_name: candFilterVendorName, evanti_spoc: candFilterEvantiSpoc,
        assigned_by: candFilterTeamLead, owner: candFilterRecruiter,
        ...dateRangeParams(entryDateFilter, 'entry'),
        ...(candidateSubTab === 'interviews' ? dateRangeParams(interviewDateFilter, 'interview', { includeBlank: true }) : {})
    }), [candidateSubTab, candSearch, globalFilters, candFilterRemark, candFilterJobRole, candFilterVendorName, candFilterEvantiSpoc, candFilterTeamLead, candFilterRecruiter, entryDateFilter, interviewDateFilter]);
    const table = usePagedQuery(`${API}/api/candidates`, candParams, { enabled: true });
    const kpiDrafts = table.rows;              // the current page (handlers below patch these rows in place)
    const setKpiDrafts = table.setRows;
    const fetchInterviews = table.refetch;
    const counts = { all: 0, candidates: 0, interviews: 0, onboarded: 0, hold: 0, interested: 0, rejected: 0, ...((table.extras && table.extras.counts) || {}) };
    const filteredCandidates = kpiDrafts;

    // Only the very first load replaces the page with "Loading..."; later filter/page changes keep the controls mounted.
    const [firstLoadDone, setFirstLoadDone] = useState(false);
    useEffect(() => { if (!table.loading) setFirstLoadDone(true); }, [table.loading]);
    const loading = !firstLoadDone;

    // Dropdown values: distinct values over everything this user can see (not just the current page).
    const [candOptions, setCandOptions] = useState({});
    useEffect(() => {
        if (!(true)) return;
        fetch(`${API}/api/candidates/filter-options`)
            .then(res => res.json())
            .then(d => setCandOptions(d && !d.error ? d : {}))
            .catch(err => console.error('Failed to fetch candidate filter options:', err));
    }, [user?.name]);
    const jobRoleOptions = candOptions.job_role || [];
    const vendorNameOptions = candOptions.vendor_name || [];
    const etSpocOptions = candOptions.evanti_spoc || [];
    const teamLeadOptions = candOptions.assigned_by || [];
    const recruiterOptions = candOptions.owner || [];

    const hasActiveFilters = candSearch.trim() || entryDateFilter.mode !== 'All' || candFilterRemark || candFilterJobRole || candFilterVendorName || candFilterEvantiSpoc || candFilterTeamLead || candFilterRecruiter || (candidateSubTab === 'interviews' && interviewDateFilter.mode !== 'All');

    const clearFilters = () => {
        setCandSearch(''); setEntryDateFilter({ mode: 'All' });
        setCandFilterRemark(''); setCandFilterJobRole('');
        setCandFilterVendorName(''); setCandFilterEvantiSpoc('');
        setCandFilterTeamLead(''); setCandFilterRecruiter('');
        setInterviewDateFilter({ mode: 'All' }); setResetSignal(x => x + 1);
    };

    const handleCallRemarkChange = async (candidateId, value) => {
        setSavingStageId(candidateId);
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, remark: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/kpi-fields`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ remark: value, requester_name: user?.name, requester_role: user?.role })
            });
            // A trigger remark may have auto-reassigned the task (item #1) — refetch so
            // that change (and the reassignment badge) shows up immediately.
            if (!response.ok) {
                fetchInterviews();
            } else {
                // Remark changed -> server stamped Updated Date; mirror it locally.
                const saved = await response.json();
                setKpiDrafts(prev => prev.map(k => k.id === candidateId
                    ? { ...k, updated_date: saved.updated_date, remark_updated_at: saved.remark_updated_at } : k));
                fetchInterviews(); // the candidate may now belong to another tab
            }
        } catch (err) {
            console.error('Failed to save call remark:', err);
            fetchInterviews();
        } finally {
            setSavingStageId(null);
        }
    };

    // Candidates tab: Mobile Number edit. A real change stamps the candidate's
    // Updated Date server-side (PATCH .../call-update); invalid/failed saves
    // revert the field.
    const handleNumberChange = async (candidateId, inputEl) => {
        const value = (inputEl.value || '').trim();
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        if (value === (current.number || '')) return;
        setSavingStageId(candidateId);
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/call-update`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ number: value, requester_name: user?.name, requester_role: user?.role })
            });
            const data = await response.json();
            if (!response.ok) {
                alert(data.error || 'Failed to update the number.');
                inputEl.value = current.number || '';
                return;
            }
            setKpiDrafts(prev => prev.map(k => k.id === candidateId
                ? { ...k, number: data.number, updated_date: data.updated_date, remark_updated_at: data.remark_updated_at }
                : k));
        } catch (err) {
            console.error('Failed to save number:', err);
            inputEl.value = current.number || '';
        } finally {
            setSavingStageId(null);
        }
    };

    const handleStageRemarkChange = async (candidateId, value) => {
        setSavingStageId(candidateId);
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, stage_remark: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stage_remark: value })
            });
            if (!response.ok) fetchInterviews(); else fetchInterviews(); // buckets / counts follow the new stage
        } catch (err) {
            console.error('Failed to save stage remark:', err);
            fetchInterviews();
        } finally {
            setSavingStageId(null);
        }
    };

    const handleInterviewDateTimeChange = async (candidateId, field, value) => {
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, [field]: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    interview_date: field === 'interview_date' ? value : current.interview_date,
                    interview_time: field === 'interview_time' ? value : current.interview_time
                })
            });
            if (!response.ok) fetchInterviews();
        } catch (err) {
            console.error('Failed to save interview date/time:', err);
            fetchInterviews();
        }
    };

    const handleDeleteCandidate = async () => {
        if (!deleteConfirmCandidate) return;
        setDeletingCandidate(true);
        try {
            const response = await fetch(
                `${API}/api/tasks/${deleteConfirmCandidate.task_id}/candidates/${deleteConfirmCandidate.id}?requester_role=${encodeURIComponent(user?.role || '')}`,
                { method: 'DELETE' }
            );
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                console.error(data.error || 'Failed to delete candidate.');
            }
            fetchInterviews();
        } catch (err) {
            console.error('Failed to delete candidate:', err);
        } finally {
            setDeletingCandidate(false);
            setDeleteConfirmCandidate(null);
        }
    };

    const candidateExportColumns = [
        { key: 'candidate_code', label: 'Candidate ID' },
        { key: 'entry_date', label: 'Date' },
        // { key: 'assigned_by', label: 'Team Lead' }, 
        { key: 'assigned_to', label: 'Recruiter' },
        // { key: 'task_display_id', label: 'Task ID' }, 
        { key: 'job_role', label: 'Job Role' },
        { key: 'req_id', label: 'Req ID' },
        { key: 'company', label: 'Company' },
        { key: 'client_spoc', label: 'Client SPOC' },
        { key: 'evanti_spoc', label: 'Evanti SPOC' },
        { key: 'name', label: 'Name' },
        { key: 'number', label: 'Number' },
        { key: 'remark', label: 'Call Remark' },
        { key: 'skill', label: 'Skill' },
        { key: 'email', label: 'Email' },
        { key: 'location', label: 'Location' },
        { key: 'current_employer', label: 'Employer' },
        { key: 'designation', label: 'Designation' },
        { key: 'total_experience_years', label: 'Exp (Yrs)' },
        { key: 'relevant_experience_years', label: 'Relevant Exp (Yrs)' },
        { key: 'notice_period', label: 'Notice Period' },
        { key: 'education', label: 'Education' },
        // { key: 'vendor_name', label: 'Vendor Name' }, 
        { key: 'bill_rate', label: 'Evanti Pay / Bill Rate' },
        { key: 'current_package', label: 'Current Pkg' },
        { key: 'expected_package', label: 'Expected Pkg' },
        { key: 'candidate_budget', label: 'Candidate Budget' },
        { key: 'client_budget', label: 'Client Budget' },
        { key: 'details_remarks', label: 'Remarks' },
        { key: 'stage_remark', label: 'Remark' },
        { key: 'interview_date', label: 'Interview Date' },
        { key: 'interview_time', label: 'Interview Time' },
        { key: 'interview_link', label: 'Interview Link' },
        { key: 'interview_link_updated_by', label: 'Link Last Changed By' }
    ];

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Interviews & Onboarding" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/admin')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Interviews & Onboarding</h2>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Loading...</div>
            ) : (
                <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px', overflowX: 'auto' }}>
                    <GlobalFilters filters={globalFilters} onChange={setGlobalFilters} rows={kpiDrafts} dateDefault="all" show={{ company: true, spoc: true, dates: false }} />

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                        {[
                            { key: 'interested', label: `Interested (${counts.interested})` },
                            { key: 'interviews', label: `Interviews (${counts.interviews})` },
                            { key: 'onboarded', label: `Onboarded Candidates (${counts.onboarded})` },
                            { key: 'hold', label: `HOLD / CLOSED (${counts.hold})` },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setCandidateSubTab(tab.key)}
                                style={{
                                    padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer',
                                    background: candidateSubTab === tab.key ? '#2563eb' : '#f1f5f9',
                                    color: candidateSubTab === tab.key ? '#fff' : '#475569'
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {candidateSubTab === 'kpi' ? (
                        <CandidateKPIDetailsGrid
                            kpiDrafts={kpiDrafts}
                            server={{ total: table.total, page: table.page, pageSize: table.pageSize, totalPages: table.totalPages, setPage: table.setPage, setPageSize: table.setPageSize, loading: table.loading, search: candSearch, setSearch: setCandSearch }}
                            currentUser={user}
                            onRowSaved={(candidateId, updatedFields) => {
                                setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, ...updatedFields } : k));
                                fetchInterviews(); // tab counts may have changed
                            }}
                        />
                    ) : (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                                        {table.total.toLocaleString()} candidate{table.total === 1 ? '' : 's'}
                                    </h3>
                                    <ExportButtons
                                        getRows={table.fetchAll}
                                        columns={candidateExportColumns}
                                        filename={`organization-candidates-${candidateSubTab}`}
                                        title="Organization Candidates Report"
                                    />
                                    {candidateSubTab === 'candidates' && (
                                        <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: '700' }}>
                                            <span style={{ color: '#059669' }}>Interviews: {counts.interviews}</span>
                                            <span style={{ color: '#dc2626' }}>Rejected: {counts.rejected}</span>
                                            <span style={{ color: '#2563eb' }}>Onboarded: {counts.onboarded}</span>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                    {/* <select style={smallSelectStyle} value={candFilterTeamLead} onChange={(e) => setCandFilterTeamLead(e.target.value)}>
                                        <option value="">All Team Leads</option>
                                        {teamLeadOptions.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select> */}
                                    <select style={smallSelectStyle} value={candFilterRecruiter} onChange={(e) => setCandFilterRecruiter(e.target.value)}>
                                        <option value="">All Recruiters</option>
                                        {recruiterOptions.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <SearchBox value={candSearch} onChange={setCandSearch} placeholder="Search name, number, email, skill, task…" minWidth="230px" />
                                    <select style={smallSelectStyle} value={candFilterJobRole} onChange={(e) => setCandFilterJobRole(e.target.value)}>
                                        <option value="">All Job Roles</option>
                                        {jobRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    {/* <select style={smallSelectStyle} value={candFilterVendorName} onChange={(e) => { setCandFilterVendorName(e.target.value); }}>
                                        <option value="">All Vendor Names</option>
                                        {vendorNameOptions.map(v => <option key={v} value={v}>{v}</option>)}
                                    </select> */}
                                    <select style={smallSelectStyle} value={candFilterEvantiSpoc} onChange={(e) => setCandFilterEvantiSpoc(e.target.value)}>
                                        <option value="">All Evanti SPOCs</option>
                                        {etSpocOptions.map(e => <option key={e} value={e}>{e}</option>)}
                                    </select>
                                    <select style={smallSelectStyle} value={candFilterRemark} onChange={(e) => setCandFilterRemark(e.target.value)}>
                                        <option value="">All Remarks</option>
                                        {STAGE_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>

                                    {hasActiveFilters && (
                                        <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                                            <X size={12} /> Clear
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* <div style={{ marginBottom: '12px' }}>
                                <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>Candidate Date</div>
                                <DateFilter key={`entry-${resetSignal}`} dateField="entry_date" initialValue={entryDateFilter} onFilterChange={setEntryDateFilter} />
                            </div> */}

                            {candidateSubTab === 'interviews' && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>Interview Date</div>
                                    <DateFilter key={`interview-${resetSignal}`} dateField="interview_date" includeBlank initialValue={interviewDateFilter} onFilterChange={setInterviewDateFilter} />
                                </div>
                            )}

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', width: 'max-content', minWidth: '100%', tableLayout: 'fixed' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                        <th style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                                        {['Recruiter', 'Current Recruiter', 'Company', 'Job Role', 'Name', 'Interview Date', 'Interview Time', 'Interview Link', 'Remark',
                                            'Evanti SPOC', 'Client SPOC', 'Candidate ID', 'Date', 'Number', 'Call Remark', 'Remarks',
                                            'Skill', 'Email', 'Location', 'Employer', 'Designation', 'Exp (Yrs)', 'Relevant Exp (Yrs)',
                                            'Notice Period', 'Education', 'Evanti Pay / Bill Rate', 'Current Pkg', 'Expected Pkg',
                                            'Candidate Budget', 'Client Budget', 'Delete'].map(h => (
                                                <th key={h} style={{
                                                    padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap',
                                                    ...(h === 'Recruiter' ? { width: '80px', minWidth: '80px', maxWidth: '80px' } : {}),
                                                    ...(h === 'Current Recruiter' ? { width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' } : {}),
                                                    ...(h === 'Job Role' ? { width: '150px', minWidth: '150px', maxWidth: '150px' } : {}),
                                                    ...(h === 'Name' ? { width: '90px', minWidth: '90px', maxWidth: '90px' } : {}),
                                                    ...(h === 'Interview Date' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                                    ...(h === 'Interview Time' ? { width: '100px', minWidth: '100px', maxWidth: '100px' } : {}),
                                                    ...(h === 'Interview Link' ? { width: '140px', minWidth: '140px', maxWidth: '140px' } : {}),
                                                    ...(h === 'Remark' ? { width: '230px', minWidth: '230px', maxWidth: '230px' } : {}),
                                                    ...(h === 'Candidate ID' ? { width: '80px', minWidth: '80px', maxWidth: '80px' } : {}),
                                                    ...(h === 'Remarks' ? { width: '330px', minWidth: '330px', maxWidth: '330px' } : {}),
                                                }}
                                                >{h}</th>
                                            ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCandidates.map((k, snoIdx) => (
                                        <tr key={k.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px', color: '#64748b', fontWeight: '600' }}>{(table.page - 1) * table.pageSize + snoIdx + 1}</td>
                                            <td style={{ padding: '8px', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.assigned_to || '-'}</td>
                                            <td style={{ padding: '8px', color: '#2563eb', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.task_assigned_to}</td>
                                            <td style={{ padding: '8px' }}>{k.company || '-'}</td>
                                            <td style={{ padding: '8px', width: '150px', minWidth: '150px', maxWidth: '150px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.job_role || 'N/A'}</td>
                                            <td style={{ padding: '8px', width: '90px', minWidth: '90px', maxWidth: '90px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.name || '-'}</td>
                                            <td style={{ padding: '8px' }}>
                                                <DateInput style={{ ...smallSelectStyle, width: '100px', minWidth: '100px', maxWidth: '100px' }} value={k.interview_date || ''} onChange={(e) => handleInterviewDateTimeChange(k.id, 'interview_date', e.target.value)} />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <input type="time" style={{ ...smallSelectStyle, width: '100px', minWidth: '100px', maxWidth: '100px' }} value={k.interview_time || ''} onChange={(e) => handleInterviewDateTimeChange(k.id, 'interview_time', e.target.value)} />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <InterviewLinkCell
                                                    row={k}
                                                    onSaved={(id, patch) => setKpiDrafts(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x))}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <select
                                                    style={{ ...smallSelectStyle, width: '230px', minWidth: '230px', maxWidth: '230px', }}
                                                    value={k.stage_remark || ''}
                                                    disabled={savingStageId === k.id}
                                                    onChange={(e) => handleStageRemarkChange(k.id, e.target.value)}
                                                >
                                                    <option value="">-</option>
                                                    {STAGE_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ padding: '8px' }}>{k.evanti_spoc || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.client_spoc || '-'}</td>
                                            <td style={{ padding: '8px', fontWeight: '700', color: '#7c3aed', width: '80px', minWidth: '80px', maxWidth: '80px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{k.candidate_code || '-'}</td>
                                            <td style={{ padding: '8px' }}>{formatDisplayDate(k.entry_date)}</td>
                                            <td style={{ padding: '8px' }}>
                                                <input
                                                    key={`${k.id}-${k.number || ''}`}
                                                    defaultValue={k.number || ''}
                                                    disabled={savingStageId === k.id}
                                                    onBlur={(e) => handleNumberChange(k.id, e.target)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                    aria-label="Mobile number"
                                                    style={{ width: '120px', padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '12px', boxSizing: 'border-box' }}
                                                />
                                            </td>
                                            <td style={{ padding: '8px' }}>
                                                <select
                                                    style={smallSelectStyle}
                                                    value={k.remark || ''}
                                                    disabled={savingStageId === k.id}
                                                    onChange={(e) => handleCallRemarkChange(k.id, e.target.value)}
                                                >
                                                    <option value="">-</option>
                                                    {CALL_REMARK_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ padding: '8px', width: '330px', minWidth: '330px', maxWidth: '330px' }}>{k.details_remarks || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.skill || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.email || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.location || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.current_employer || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.designation || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.total_experience_years ?? '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.relevant_experience_years ?? '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.notice_period || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.education || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.bill_rate || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.current_package || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.expected_package || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.candidate_budget || '-'}</td>
                                            <td style={{ padding: '8px' }}>{k.client_budget || '-'}</td>
                                            <td style={{ padding: '8px' }}>
                                                <button
                                                    onClick={() => setDeleteConfirmCandidate(k)}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2',
                                                        color: '#b91c1c', border: 'none', padding: '5px 8px', borderRadius: '4px',
                                                        fontSize: '11px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredCandidates.length === 0 && (
                                        <tr><td colSpan={33} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No candidates found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                            <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                                onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="candidates" />
                        </>
                    )}
                </div>
            )}

            {deleteConfirmCandidate && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Delete Candidate?</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                            This will permanently delete <strong>{deleteConfirmCandidate.name || deleteConfirmCandidate.candidate_code}</strong> from task <strong>{deleteConfirmCandidate.task_display_id}</strong>. This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setDeleteConfirmCandidate(null)}
                                disabled={deletingCandidate}
                                style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCandidate}
                                disabled={deletingCandidate}
                                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: deletingCandidate ? 'not-allowed' : 'pointer', opacity: deletingCandidate ? 0.7 : 1 }}
                            >
                                {deletingCandidate ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminInterviews;